import type { AnalyticsEventMetadata } from '@resistapp/common/api-types';
import styled from '@emotion/styled';
import { oldTheme } from '@resistapp/client/components/shared/old-styles';
import { useProjectImage } from '@resistapp/client/hooks/api';
import { Spinner } from '@chakra-ui/react';
import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useAnalytics } from '../../contexts/analytics-context';
import { DownloadIcon } from '../icons/download-icon';
import { resolveDownloadTrackingLocation, shouldConsumeDownloadedImage } from './download-link-utils';

interface Props {
  filename: string;
  getFileContent?: () => Promise<string | undefined> | string | undefined; // Either getFileContent or projectId required
  projectId?: number;
  analyticsMetadata?: AnalyticsEventMetadata & { downloadKind: 'result' | 'graph' | 'legend' };
}

export function DownloadLink({ projectId, filename, getFileContent, analyticsMetadata }: Props) {
  const { data: image, mutate: download } = useProjectImage(projectId || 0);
  const { trackDownload } = useAnalytics();
  const location = useLocation();
  const lastDownloadedImageRef = useRef<Blob | undefined>(undefined);
  const pendingDownloadSnapshotRef = useRef<{
    locationSnapshot: { pathname: string; search: string };
    occurredAt: string;
  } | null>(null);

  useEffect(() => {
    if (!shouldConsumeDownloadedImage(image, lastDownloadedImageRef.current)) {
      return;
    }

    lastDownloadedImageRef.current = image;
    clickDownloadElement(image, filename);
    if (analyticsMetadata) {
      const pendingDownloadSnapshot = pendingDownloadSnapshotRef.current;
      trackDownload({
        ...analyticsMetadata,
        occurredAt: pendingDownloadSnapshot?.occurredAt,
        locationSnapshot: resolveDownloadTrackingLocation(pendingDownloadSnapshot?.locationSnapshot ?? null, {
          pathname: location.pathname,
          search: location.search,
        }),
      });
    }
    pendingDownloadSnapshotRef.current = null;
  }, [analyticsMetadata, filename, image, location.pathname, location.search, trackDownload]);

  // Preparing the content can take a while when it is loaded from the API on demand (e.g. the raw Cq files)
  const [isPreparing, setIsPreparing] = useState(false);

  return (
    <Root>
      <Link
        onClick={() => {
          if (isPreparing) {
            return;
          }
          void (async () => {
            const downloadSnapshot = {
              occurredAt: new Date().toISOString(),
              locationSnapshot: {
                pathname: location.pathname,
                search: location.search,
              },
            };
            setIsPreparing(true);
            try {
              const fileContent = getFileContent ? await getFileContent() : undefined;
              const hasInlineContent = fileContent !== undefined;
              if (hasInlineContent) {
                clickDownloadElement(fileContent, filename);
                if (analyticsMetadata) {
                  trackDownload({
                    ...analyticsMetadata,
                    ...downloadSnapshot,
                  });
                }
              } else if (projectId) {
                pendingDownloadSnapshotRef.current = downloadSnapshot;
                download({ filename });
              }
            } finally {
              setIsPreparing(false);
            }
          })();
        }}
      >
        <DownloadIcon size="md" style={{ paddingTop: '4px' }} />
        {filename}
        {isPreparing && <Spinner size="xs" marginTop="5px" />}
      </Link>
    </Root>
  );
}

const clickDownloadElement = (content: string | Blob, filename: string) => {
  if (content) {
    const element = document.createElement('a');
    const file = typeof content === 'string' ? new Blob([content], { type: 'text/html' }) : content;
    element.href = URL.createObjectURL(file);
    element.download = filename;
    document.body.appendChild(element); // Required for this to work in FireFox
    element.click();
  }
};

const Root = styled.div`
  color: ${oldTheme.lightBlue};
  font-size: ${oldTheme.fontSize.s};
  margin-left: ${oldTheme.spacing.xxs};
`;

const Link = styled.a`
  display: flex;
  gap: 5px;
  cursor: pointer;
  color: gray;
  &:hover {
    color: ${oldTheme.black};
  }
`;
